import React from 'react';
import { PageHeader } from '../components/States';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Building2, ListTree, SlidersHorizontal, Layers, Search, Upload, Calculator,
  GitCompare, Pencil, Sparkles, History, Lock, Info, ShieldCheck, Lightbulb, ArrowRight,
} from 'lucide-react';

const SECTIONS = [
  {
    key: 'anagrafiche', n: 1, icon: Building2, title: 'Anagrafiche', tagline: 'Prepara le basi',
    body: 'Due schede: Società (le aziende del gruppo che poi consoliderai) e Piano dei conti / mastrini (i conti contabili con codice e descrizione).',
    hint: 'Da impostare una volta sola, poi si aggiorna solo quando cambia qualcosa.',
    items: [{ i: Building2, t: 'Società', d: 'Elenco aziende del gruppo' }, { i: ListTree, t: 'Piano dei conti', d: 'Mastrini con codice e descrizione' }],
  },
  {
    key: 'regole', n: 2, icon: SlidersHorizontal, title: 'Schemi & Regole', tagline: 'Come leggere i conti',
    body: 'Lo Schema è il Conto Economico riclassificato (Ricavi, Costi, MOL, ecc.): puoi partire dallo schema di default. Le Regole dicono come ogni conto finisce in una voce riclassificata.',
    hint: 'Imposti le regole una volta; da lì la riclassifica è automatica.',
    items: [{ i: ListTree, t: 'Prefisso codice', d: 'Es. tutti i conti che iniziano per 60' }, { i: Search, t: 'Parola chiave', d: 'Es. descrizione contiene "infragruppo"' }],
  },
  {
    key: 'analisi', n: 3, icon: Layers, title: 'Analisi di gruppo', tagline: 'Il cuore del lavoro',
    body: 'Crei un\'Analisi (es. Base 2025, H1 2026) indicando nome, anno, mesi da–a e società incluse. Ogni analisi è consolidata e versionata; aprendola trovi 5 schede operative.',
    hint: 'La prima analisi definisce la "Base" usata per i confronti (Delta).',
    items: null,
  },
  {
    key: 'partitari', n: 4, icon: Search, title: 'Partitari (drill-down)', tagline: 'Scendi nel dettaglio',
    body: 'Esplori le singole scritture di ogni conto e le partite intercompany.',
    hint: 'I Partitari NON alimentano il consolidato: servono solo per drill-down e per verificare le elisioni.',
    items: null,
  },
];

const TABS = [
  { icon: Upload, title: 'Dati & Calcolo', desc: 'Carichi il bilancio/partitario di ogni società, poi premi Calcola: il sistema riclassifica secondo Schema + Regole e genera una versione (v1, v2…).' },
  { icon: GitCompare, title: 'Consolidato & Delta', desc: 'Vedi il consolidato di gruppo con elisione delle partite intercompany (riconciliazione) e il Delta rispetto alla versione Base.' },
  { icon: Pencil, title: 'Rettifiche di periodo', desc: 'Aggiungi aggiustamenti manuali (es. SAL cantiere, magazzino provvisorio): società, importo, nota. Poi ricalcola per applicarle.' },
  { icon: Sparkles, title: 'Analisi AI', desc: 'Ottieni un commento automatico. Puoi porre una domanda (es. "Perché il MOL è calato rispetto alla Base?") o lasciarla vuota per un commento generale.' },
  { icon: History, title: 'Versioni', desc: 'Storico dei calcoli. Puoi bloccare la Base: da quel momento il ricalcolo è riservato all\'amministratore.' },
];

const FLOW = [
  'Anagrafiche → verifica società e piano dei conti',
  'Schemi & Regole → conferma/adegua schema e regole di mappatura',
  'Analisi di gruppo → crea l\'analisi (periodo + società)',
  'Dati & Calcolo → carica i bilanci di ogni società → Calcola',
  'Rettifiche di periodo → inserisci aggiustamenti → ricalcola',
  'Consolidato & Delta → leggi il consolidato e il confronto con la Base',
  'Analisi AI → genera il commento',
  'Versioni → blocca la Base quando è definitiva ed esporta il report',
];

const TIPS = [
  'La prima analisi definisce la "Base": bloccala solo quando è consolidata, così i Delta sono affidabili.',
  'Ogni ricalcolo crea una nuova versione: puoi sempre confrontarle tra loro.',
  'Dopo aver aggiunto o rimosso una rettifica, ricorda di ricalcolare per aggiornare i numeri.',
  'Usa i Partitari per giustificare le elisioni intercompany, ma ricorda che non modificano il consolidato.',
];

export default function GuidaCGAvanzato() {
  return (
    <div className="space-y-6 max-w-4xl" data-testid="guida-cga-page">
      <PageHeader title="Guida CG Avanzato" description="Mini-manuale operativo del Controllo di Gestione Avanzato, pensato per il Controller." />

      {/* A cosa serve */}
      <Card className="p-5 bg-primary/5 border-primary/20" data-testid="guida-intro">
        <div className="flex items-start gap-3">
          <span className="h-9 w-9 rounded-lg bg-primary/10 text-primary grid place-items-center shrink-0"><Info className="h-5 w-5" /></span>
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-1">A cosa serve</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">Riclassifica i bilanci delle società del gruppo, li <span className="font-medium text-foreground">consolida</span> (con elisione delle partite infragruppo), li <span className="font-medium text-foreground">confronta nel tempo</span> (versioni e Delta rispetto alla Base) e produce un <span className="font-medium text-foreground">commento AI</span>. È lo strumento che ha sostituito il vecchio "Controllo di Gestione".</p>
          </div>
        </div>
      </Card>

      {/* Le 4 sezioni */}
      <div>
        <h2 className="text-sm font-semibold text-foreground uppercase tracking-wide mb-3">Le 4 voci di menu (in ordine d'uso)</h2>
        <div className="grid grid-cols-12 gap-4">
          {SECTIONS.map((s) => (
            <Card key={s.key} className="col-span-12 md:col-span-6 p-5" data-testid={`guida-section-${s.key}`}>
              <div className="flex items-center gap-3 mb-2">
                <span className="h-9 w-9 rounded-lg bg-muted grid place-items-center text-foreground shrink-0"><s.icon className="h-5 w-5" /></span>
                <div>
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="text-[10px]">{s.n}</Badge>
                    <h3 className="text-sm font-semibold text-foreground">{s.title}</h3>
                  </div>
                  <p className="text-[11px] text-muted-foreground">{s.tagline}</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
              {s.items && (
                <div className="mt-3 space-y-2">
                  {s.items.map((it, i) => (
                    <div key={i} className="flex items-start gap-2 rounded-md border bg-muted/30 px-3 py-2">
                      <it.i className="h-4 w-4 text-muted-foreground mt-0.5 shrink-0" />
                      <div><div className="text-xs font-medium text-foreground">{it.t}</div><div className="text-[11px] text-muted-foreground">{it.d}</div></div>
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-3 flex items-start gap-1.5 text-[11px] text-muted-foreground border-t pt-2">
                <Lightbulb className="h-3.5 w-3.5 mt-0.5 shrink-0 text-[hsl(var(--primary))]" />
                <span>{s.hint}</span>
              </div>
            </Card>
          ))}
        </div>
      </div>

      {/* Le 5 schede dell'analisi */}
      <Card className="p-5" data-testid="guida-tabs">
        <h2 className="text-sm font-semibold text-foreground mb-1">Dentro un'Analisi: le 5 schede</h2>
        <p className="text-xs text-muted-foreground mb-4">Ciò che trovi aprendo una singola analisi di gruppo. Da qui puoi anche esportare (Excel/CSV) e generare un Report.</p>
        <div className="space-y-2.5">
          {TABS.map((t, i) => (
            <div key={i} className="flex items-start gap-3 rounded-lg border px-3 py-2.5" data-testid={`guida-tab-${i}`}>
              <span className="h-8 w-8 rounded-md bg-muted grid place-items-center text-foreground shrink-0"><t.icon className="h-4 w-4" /></span>
              <div className="min-w-0">
                <div className="text-sm font-medium text-foreground">{t.title}</div>
                <div className="text-xs text-muted-foreground leading-relaxed">{t.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Flusso operativo */}
      <Card className="p-5" data-testid="guida-flow">
        <div className="flex items-center gap-2 mb-3"><Calculator className="h-4 w-4 text-primary" /><h2 className="text-sm font-semibold text-foreground">Flusso operativo consigliato</h2></div>
        <ol className="space-y-2">
          {FLOW.map((step, i) => (
            <li key={i} className="flex items-start gap-3 text-sm">
              <span className="h-6 w-6 rounded-full bg-primary/10 text-primary grid place-items-center text-xs font-semibold shrink-0">{i + 1}</span>
              <span className="text-muted-foreground pt-0.5">{step}</span>
            </li>
          ))}
        </ol>
      </Card>

      {/* Ruoli + Consigli */}
      <div className="grid grid-cols-12 gap-4">
        <Card className="col-span-12 md:col-span-6 p-5" data-testid="guida-roles">
          <div className="flex items-center gap-2 mb-3"><ShieldCheck className="h-4 w-4 text-primary" /><h2 className="text-sm font-semibold text-foreground">Ruoli</h2></div>
          <div className="space-y-3 text-sm">
            <div>
              <div className="font-medium text-foreground">Controller</div>
              <p className="text-xs text-muted-foreground">Crea analisi, carica dati, calcola, inserisce rettifiche, consulta ed esporta.</p>
            </div>
            <div>
              <div className="flex items-center gap-1.5 font-medium text-foreground"><Lock className="h-3.5 w-3.5" />Direzione (admin)</div>
              <p className="text-xs text-muted-foreground">Può ricalcolare anche le analisi con Base bloccata.</p>
            </div>
          </div>
        </Card>
        <Card className="col-span-12 md:col-span-6 p-5" data-testid="guida-tips">
          <div className="flex items-center gap-2 mb-3"><Lightbulb className="h-4 w-4 text-primary" /><h2 className="text-sm font-semibold text-foreground">Consigli pratici</h2></div>
          <ul className="space-y-2">
            {TIPS.map((tip, i) => (
              <li key={i} className="flex items-start gap-2 text-xs text-muted-foreground">
                <ArrowRight className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" />
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </Card>
      </div>
    </div>
  );
}
